import React, { useState, useCallback, memo } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Avatar,
  Tooltip,
  Chip,
  Fade,
  useTheme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import AssignmentIcon from '@mui/icons-material/Assignment';

interface HeaderProps {
  projectName: string;
  onMenuClick?: () => void; // Now optional
  onAdminClick?: () => void;
  onDashboardToggle?: () => void;
  useCubaDashboard?: boolean;
}

const Header = memo<HeaderProps>(({ 
  projectName, 
  onMenuClick, 
  onAdminClick, 
  onDashboardToggle,
  useCubaDashboard = false 
}) => {
  const theme = useTheme();
  const [hover, setHover] = useState(false);
  
  const handleMouseEnter = useCallback(() => {
    setHover(true);
  }, []);
  
  const handleMouseLeave = useCallback(() => {
    setHover(false);
  }, []);
  
  return (
    <AppBar 
      position="static" 
      elevation={4}
      sx={{ 
        background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
        borderBottom: `1px solid ${theme.palette.primary.dark}`,
      }}
      component="header"
      role="banner"
    >
      <Toolbar sx={{ minHeight: '64px' }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center' 
          }}
        >
          {onMenuClick && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={onMenuClick}
              aria-haspopup="true"
              sx={{ 
                mr: 2,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
                '&:focus-visible': {
                  outline: '2px solid white',
                  outlineOffset: '2px'
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <AssignmentIcon 
            sx={{ 
              mr: 1, 
              fontSize: 28, 
              display: { xs: 'none', sm: 'block' },
              color: 'white',
              filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.2))'
            }} 
            aria-hidden="true"
          />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 600,
              letterSpacing: '0.5px',
              textShadow: '0px 1px 2px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: { xs: '180px', sm: '300px', md: '500px' }
            }}
          >
            {projectName}
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            mx: 2
          }}
        >
          <Fade in={hover}>
            <Chip 
              icon={<DashboardIcon fontSize="small" />}
              label="Requirements Tracker"
              color="secondary"
              variant="outlined"
              size="small"
              sx={{ 
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                opacity: hover ? 1 : 0,
                transition: 'opacity 0.3s ease',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                cursor: 'default'
              }}
              aria-hidden="true"
            />
          </Fade>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {onDashboardToggle && (
            <Tooltip title="Toggle Dashboard View">
              <ToggleButtonGroup
                value={useCubaDashboard ? 'cuba' : 'standard'}
                exclusive
                onChange={onDashboardToggle}
                aria-label="dashboard view"
                size="small"
                sx={{
                  mr: 1,
                  '& .MuiToggleButton-root': {
                    color: 'rgba(255,255,255,0.8)',
                    borderColor: 'rgba(255,255,255,0.3)',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                      borderColor: 'rgba(255,255,255,0.5)',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.08)',
                    },
                  }
                }}
              >
                <ToggleButton value="standard" aria-label="standard dashboard view">
                  <DashboardIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="cuba" aria-label="CUBA dashboard view">
                  <ViewQuiltIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}
          {onAdminClick && (
            <Tooltip title="Admin Settings">
              <Button 
                variant="outlined"
                color="inherit" 
                onClick={onAdminClick}
                startIcon={<AdminPanelSettingsIcon />}
                size="small"
                aria-label="Open admin settings"
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  borderColor: 'rgba(255,255,255,0.5)',
                  mr: 1,
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.8)',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid white',
                    outlineOffset: '2px'
                  },
                }}
              >
                Admin
              </Button>
            </Tooltip>
          )}
          
          {onAdminClick && (
            <Tooltip title="Admin Settings">
              <IconButton 
                color="inherit" 
                onClick={onAdminClick}
                size="small"
                aria-label="Open admin settings"
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  borderColor: 'rgba(255,255,255,0.5)',
                  border: '1px solid',
                  mr: 1,
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.8)',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid white',
                    outlineOffset: '2px'
                  },
                }}
              >
                <AdminPanelSettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          <Avatar 
            alt="User Account" 
            sx={{ 
              width: 36, 
              height: 36,
              bgcolor: theme.palette.secondary.main,
              cursor: 'pointer',
              boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
              transition: 'transform 0.2s ease',
              '&:hover': {
                transform: 'scale(1.1)'
              },
              '&:focus-visible': {
                outline: '2px solid white',
                outlineOffset: '2px'
              }
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            tabIndex={0}
            role="button"
            aria-label="User account"
          >
            UR
          </Avatar>
        </Box>
      </Toolbar>
    </AppBar>
  );
});

export default Header;
